import type { ICustomer, IBaseCustomer } from './customers';
import type {
  IBaseInvoice,
  IPaymentReversal,
  IPaymentAllocation,
  InvoicePaymentStatus,
  InvoiceStatus,
} from './invoices';

export type Suggestion = {
  percent: number;
  reason: string;
};

export type IAIPaymentSuggestion = {
  id: number;
  paymentId: string;
  invoiceId: string;
  totalConfidence: string;
  customerNameConfidence: Suggestion;
  customerAddressConfidence: Suggestion;
  invoiceAmountConfidence: Suggestion;
  dueDateConfidence: Suggestion;
  referenceConfidence: Suggestion;
  currencyConfidence: Suggestion;
  reasoning: string;
  invoice: Omit<
    IBaseInvoice,
    | 'customer'
    | 'client'
    | 'status'
    | 'paymentStatus'
    | 'countryCode'
    | 'currency'
    | 'totalAmount'
    | 'ssn'
  > & {
    customer: Pick<IBaseCustomer, 'id' | 'customerNo' | 'customerName'>;
  };
  payment: {
    currency: string;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type IPayment = {
  id: string;
  invoiceId?: string;
  paymentAllocationId?: string;
  status: PaymentStatus;
  statusReason: PaymentStatusReason;
  amount: number;
  currency: string;
  paidDate: string;
  transactionReference: string;
  paymentReference?: string;
  transactionType?: PaymentTransactionType;
  clientId: string;
  transactionProvider: PaymentTransactionProvider;
  invoiceNo?: string;
  rawFormat?: IRawFormat;
  customer?: ICustomer;
  customerSortProp: string;
  appUrl?: string;
  additionalInformation?: string;
  moveToNotMatchedReason: MoveToNotMatchedReason | null;
  isReversible?: boolean | null;
  reversals?: IPaymentReversal[];
  allocations?: IPaymentAllocation[];
  unallocatedAmount?: number;
  invoiceStatus?: InvoiceStatus;
  invoicePaymentStatus: InvoicePaymentStatus;
  expectedPaymentStatus: InvoicePaymentStatus;
  metadata: Record<string, unknown>;
  // deviceType: string;
  // callbackUrl: string;
  // payerAlias: string;
  // payeeAlias: string;
  // errorMessage: string;
  // token: string;
  // paymentBreakdown: {
  //     fee: number,
  //     capital: number,
  //     interest: number
  // },
  // createdAt: string;
  // updatedAt: string;
  // deletedAt: string;
  // address?: string;
  // phoneNumber?: string;
  // message?: string;
  // customerSSN?: string;
  // customerId: string;
  // customerName: string;
};

export enum MoveToNotMatchedReason {
  DoublePayment = 'doublePayment',
  OverPayment = 'overPayment',
  UnknownReference = 'unknownReference',
  CreditInvoiceUtilized = 'creditInvoiceUtilized',
  Other = 'other',
}

export type IRawFormat = {
  creditor: string;
  messageId: string;
  bookingDate: string;
  creditorBank: {
    id: string;
    schemaName: string;
  };
  debitorBank: {
    id: string;
    schemaName: string;
    name: string;
  };
  debitorName: string;
  debitorAddress: string;
  instructionId: string;
  accountDetails: {
    id: string;
    name: string;
    currency: string;
  };
  parsedFileName: string;
  creationDateTime: string;
  accountServicerReference: string;
};

export enum PaymentStatus {
  NotProcessed = 'not_processed',
  MatchedManual = 'matched_manual',
  MatchedAuto = 'matched_auto',
  NotMatched = 'not_matched',
}

export enum PaymentStatusReason {
  AlreadyPaid = 'already_paid',
  InDebtCollection = 'in_debt_collection',
  DuplicatePaymentReference = 'duplicate_payment_reference',
  PaymentIntention = 'payment_intention',
  CurrencyMismatch = 'currency_mismatch',
  Overpaid = 'overpayment',
  SwishDeclined = 'swish_declined',
  SwishError = 'swish_error',
}

export enum PaymentTransactionType {
  GoodWill = 'good_will',
  CreditLoss = 'credit_loss',
  CreditAmount = 'credit_amount',
  DirectPayment = 'direct_payment',
  Cancelled = 'cancelled',
  Bank = 'bank',
  PSP = 'psp',
  DirectDebit = 'direct_debit',
  DebtCollection = 'debt_collection',
  ReminderFeeWaiver = 'reminder_fee_waiver',
  PaymentToDC = 'payment_to_dc',
  CreditAmountToDC = 'credit_amount_to_dc',
  GoodwillWaiveToDC = 'goodwill_waive_to_dc',
}

export enum PaymentTransactionProvider {
  Swish = 'swish',
  Camt54 = 'camt54',
  Camt53 = 'camt53',
  Manual = 'manual',
  DirectDebit = 'direct_debit',
  Unknown = 'unknown',
}
